"use client"

import {
  type ISupportedWallet,
  LocalStorageKeys,
  type Networks as StellarWalletNetwork,
  StellarWalletsKit,
} from "@creit.tech/stellar-wallets-kit"
import {
  FREIGHTER_ID,
  FreighterModule,
} from "@creit.tech/stellar-wallets-kit/modules/freighter"
import { HotWalletModule } from "@creit.tech/stellar-wallets-kit/modules/hotwallet"
import { LobstrModule } from "@creit.tech/stellar-wallets-kit/modules/lobstr"
import { xBullModule } from "@creit.tech/stellar-wallets-kit/modules/xbull"
import { activeAddress } from "@creit.tech/stellar-wallets-kit/state"
import { base64 } from "@scure/base"
import { useLastConnectedWalletStore } from "@src/stores/useLastConnectedWalletStore"
import { Horizon, Networks, TransactionBuilder } from "@stellar/stellar-sdk"
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"

interface StellarWalletContextType {
  publicKey: string | null
  connectedWalletId: string | null
  isLoading: boolean
  error: string | null
  isConnected: boolean
  connect: (walletId?: string) => Promise<void>
  disconnect: () => Promise<void>
  fetchPublicKey: () => Promise<void>
  resetPublicKey: () => void
  clearError: () => void
  availableWallets: readonly ISupportedWallet[]
  refreshAvailableWallets: () => Promise<void>
}

const StellarWalletContext = createContext<StellarWalletContextType | null>(
  null
)

let initialized = false

function initKit() {
  if (!initialized) {
    StellarWalletsKit.init({
      modules: [
        new FreighterModule(),
        new HotWalletModule(),
        new LobstrModule(),
        new xBullModule(),
      ],
      network: Networks.PUBLIC as StellarWalletNetwork,
      selectedWalletId: getSelectedWalletId() ?? FREIGHTER_ID,
    })
    initialized = true
  }
}

export const signTransactionStellar = async (
  ...args: Parameters<typeof StellarWalletsKit.signTransaction>
) => {
  initKit()
  return StellarWalletsKit.signTransaction(...args)
}

export const submitTransactionStellar = async (signedTxXdr: string) => {
  const server = new Horizon.Server("https://horizon.stellar.org")
  const transaction = TransactionBuilder.fromXDR(signedTxXdr, Networks.PUBLIC)
  const response = await server.submitTransaction(transaction)
  return response.hash
}

export const signMessageStellar = async (
  message: string
): Promise<Uint8Array> => {
  initKit()
  const { signedMessage } = await StellarWalletsKit.signMessage(message)
  return base64.decode(signedMessage)
}

export async function getPublicKeyStellar() {
  if (!getSelectedWalletId()) return null
  initKit()
  const { address } = await StellarWalletsKit.getAddress()
  return address
}

const STELLAR_SELECTED_WALLET_ID = "stellar-selected-wallet-id"
const STELLAR_PUBLIC_KEY = "stellar-public-key"

function getSelectedWalletId() {
  return (
    localStorage.getItem(LocalStorageKeys.selectedModuleId) ??
    localStorage.getItem(STELLAR_SELECTED_WALLET_ID)
  )
}

function getCachedPublicKey() {
  return localStorage.getItem(STELLAR_PUBLIC_KEY)
}

function setCachedPublicKey(publicKey: string) {
  localStorage.setItem(STELLAR_PUBLIC_KEY, publicKey)
}

function clearCachedPublicKey() {
  localStorage.removeItem(STELLAR_PUBLIC_KEY)
}

export function setWalletStellar(walletId: string) {
  initKit()
  StellarWalletsKit.setWallet(walletId)
}

export async function disconnectStellar() {
  localStorage.removeItem(STELLAR_SELECTED_WALLET_ID)
  clearCachedPublicKey()
  initKit()
  await StellarWalletsKit.disconnect()
}

export async function connectStellar(): Promise<string> {
  initKit()
  const { address } = await StellarWalletsKit.authModal()
  return address
}

const getErrorMessage = (error: unknown): string => {
  return error instanceof Error ? error.message : String(error)
}

export function StellarWalletProvider({
  children,
}: { children: React.ReactNode }) {
  const [publicKey, setPublicKey] = useState<string | null>(null)
  const [connectedWalletId, setConnectedWalletId] = useState<string | null>(
    null
  )
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [availableWallets, setAvailableWallets] = useState<
    readonly ISupportedWallet[]
  >([])

  const refreshAvailableWallets = useCallback(async () => {
    initKit()
    const wallets = await StellarWalletsKit.refreshSupportedWallets()
    setAvailableWallets(wallets)
  }, [])

  const fetchPublicKey = async () => {
    setIsLoading(true)
    setError(null)

    try {
      const key = await getPublicKeyStellar()
      setPublicKey(key)
    } catch (err) {
      setError(getErrorMessage(err))
      setPublicKey(null)
    } finally {
      setIsLoading(false)
    }
  }

  const resetPublicKey = () => {
    setPublicKey(null)
    setError(null)
    setIsLoading(false)
  }

  const clearError = () => {
    setError(null)
  }

  const connect = async (walletId?: string) => {
    setIsLoading(true)
    setError(null)

    try {
      initKit()
      let address: string
      if (walletId) {
        StellarWalletsKit.setWallet(walletId)
        const { address: fetched } =
          await StellarWalletsKit.selectedModule.getAddress()
        activeAddress.value = fetched
        address = fetched
      } else {
        const result = await StellarWalletsKit.authModal()
        address = result.address
      }
      setCachedPublicKey(address)
      setPublicKey(address)
      const connectedWalletId = walletId ?? getSelectedWalletId() ?? null
      setConnectedWalletId(connectedWalletId)
      if (connectedWalletId) {
        useLastConnectedWalletStore.getState().recordLastConnectedWallet({
          chain: "stellar",
          walletId: connectedWalletId,
        })
      }
    } catch (err) {
      const message = getErrorMessage(err)
      if (!/cancelled/i.test(message)) {
        setError(message)
        throw err
      }
    } finally {
      setIsLoading(false)
    }
  }

  const disconnect = async () => {
    setIsLoading(true)

    try {
      await disconnectStellar()
      resetPublicKey()
      setConnectedWalletId(null)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setIsLoading(false)
    }
  }

  // Initialize wallet state on mount using cached public key
  useEffect(() => {
    const cachedKey = getCachedPublicKey()
    const walletId = getSelectedWalletId()
    if (cachedKey && walletId) {
      setPublicKey(cachedKey)
      setConnectedWalletId(walletId)
    }
  }, [])

  // Note: value is intentionally recreated on every render — connect /
  // disconnect / fetchPublicKey / resetPublicKey / clearError aren't
  // memoized. The fix Codex flagged is `refreshAvailableWallets`, which
  // is now useCallback-stable so the modal's useEffect doesn't loop.
  const value: StellarWalletContextType = {
    publicKey,
    connectedWalletId,
    isLoading,
    error,
    isConnected: !!publicKey,
    connect,
    disconnect,
    fetchPublicKey,
    resetPublicKey,
    clearError,
    availableWallets,
    refreshAvailableWallets,
  }

  return (
    <StellarWalletContext.Provider value={value}>
      {children}
    </StellarWalletContext.Provider>
  )
}

export function useStellarWallet() {
  const context = useContext(StellarWalletContext)
  if (!context) {
    throw new Error(
      "useStellarWallet must be used within a StellarWalletProvider"
    )
  }
  return context
}
